import { createSpatialIndex } from './spatialIndex';
import {
  DEFAULT_CONNECTION_STYLE_ID,
  normalizeConnectionStyleId,
} from '../../config/connectionStyles';

const SIDES = ['right', 'bottom', 'left', 'top'];
const SIDE_VECTORS = {
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  top: { x: 0, y: -1 },
  bottom: { x: 0, y: 1 },
};
const DEFAULT_CLEARANCE = 12;
const DEFAULT_EDGE_LANE_CLEARANCE = 12;
const DEFAULT_LEAD = 24;
const DEFAULT_CORNER_RADIUS = 18;
const ORTHOGONAL_CORNER_RADIUS = 12;
const MAX_EDGE_DETOUR_RATIO = 1.3;
const MIN_EDGE_DETOUR_ALLOWANCE = 96;
const ROUTE_HYSTERESIS_LENGTH_RATIO = 1.08;
const ROUTE_HYSTERESIS_LENGTH_ALLOWANCE = 24;
export const MAX_OBSTACLE_ROUTING_NODES = 80;
const EPSILON = 1e-7;
export const EDGE_ENDPOINT_OVERLAP = 4;

export function rectCenter(rect) {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

export function rectBoundaryAnchor(rect, toward) {
  const center = rectCenter(rect);
  const dx = toward.x - center.x;
  const dy = toward.y - center.y;

  if (dx === 0 && dy === 0) return center;

  const halfWidth = Math.max(rect.width / 2, 0.5);
  const halfHeight = Math.max(rect.height / 2, 0.5);
  const scale = 1 / Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);

  return {
    x: center.x + dx * scale,
    y: center.y + dy * scale,
  };
}

export function rectSideAnchor(rect, side) {
  const center = rectCenter(rect);
  if (side === 'left') return { x: rect.x, y: center.y };
  if (side === 'right') return { x: rect.x + rect.width, y: center.y };
  if (side === 'top') return { x: center.x, y: rect.y };
  if (side === 'bottom') return { x: center.x, y: rect.y + rect.height };
  throw new TypeError(`Unsupported rectangle side: ${side}`);
}

export function inflateRect(rect, amount = DEFAULT_CLEARANCE) {
  return {
    x: rect.x - amount,
    y: rect.y - amount,
    width: rect.width + amount * 2,
    height: rect.height + amount * 2,
  };
}

function oppositeSide(side) {
  if (side === 'left') return 'right';
  if (side === 'right') return 'left';
  if (side === 'top') return 'bottom';
  return 'top';
}

export function connectionSide(fromRect, toRect) {
  const fromCenter = rectCenter(fromRect);
  const toCenter = rectCenter(toRect);
  const dx = toCenter.x - fromCenter.x;
  const dy = toCenter.y - fromCenter.y;

  if (dx === 0 && dy === 0) return 'right';

  // Keep a horizontally separated mind-map branch attached to the parent's
  // facing horizontal side even when a tall sibling fan makes the vertical
  // centre distance larger. Otherwise the outer siblings abruptly switch to
  // top/bottom ports and their curves appear as straight diagonals.
  const isHorizontallySeparated = (
    toRect.x > fromRect.x + fromRect.width + EPSILON
    || fromRect.x > toRect.x + toRect.width + EPSILON
  );
  if (isHorizontallySeparated) return dx < 0 ? 'left' : 'right';

  // A left/right port pair leaves one node's side edge and arrives at the
  // other's opposite side edge. When the nodes overlap on the horizontal axis
  // that path has to double back on itself, and the connection renders as a
  // visible loop beside the node.
  //
  // Slight overlap is tolerable and still reads as a sideways branch, so the
  // test is whether a node's centre lies within the other's horizontal span.
  // Past that point the backtrack runs behind the middle of a node and there
  // is no sideways reading left to preserve, while the vertical gap gives a
  // clean top/bottom attachment.
  const isVerticallySeparated = (
    toRect.y > fromRect.y + fromRect.height + EPSILON
    || fromRect.y > toRect.y + toRect.height + EPSILON
  );
  const centreWithinSpan = (
    (toCenter.x > fromRect.x && toCenter.x < fromRect.x + fromRect.width)
    || (fromCenter.x > toRect.x && fromCenter.x < toRect.x + toRect.width)
  );
  if (isVerticallySeparated && centreWithinSpan) {
    return dy < 0 ? 'top' : 'bottom';
  }

  // When wide nodes overlap slightly on the horizontal axis, a vertical-gap
  // check used to force bottom/top ports even if the child was mostly to the
  // side. The centre direction is the shorter and more legible choice there.
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx < 0 ? 'left' : 'right';
  }
  return dy < 0 ? 'top' : 'bottom';
}

function cubicSegment(from, control1, control2, to) {
  return { from, control1, control2, to };
}

function lineAsCubic(from, to) {
  return cubicSegment(
    from,
    {
      x: from.x + (to.x - from.x) / 3,
      y: from.y + (to.y - from.y) / 3,
    },
    {
      x: from.x + ((to.x - from.x) * 2) / 3,
      y: from.y + ((to.y - from.y) * 2) / 3,
    },
    to
  );
}

function portTangentCubic(from, to, sourceSide, targetSide) {
  const sourceVector = SIDE_VECTORS[sourceSide];
  const targetVector = SIDE_VECTORS[targetSide];
  const bend = Math.max(32, Math.min(pointDistance(from, to) * 0.35, 220));
  return cubicSegment(
    from,
    {
      x: from.x + sourceVector.x * bend,
      y: from.y + sourceVector.y * bend,
    },
    {
      x: to.x + targetVector.x * bend,
      y: to.y + targetVector.y * bend,
    },
    to
  );
}

function segmentPath(segment) {
  return `C ${segment.control1.x} ${segment.control1.y}, ${segment.control2.x} ${segment.control2.y}, ${segment.to.x} ${segment.to.y}`;
}

function inwardDelta(side, amount) {
  const outward = SIDE_VECTORS[side];
  return {
    x: -outward.x * amount,
    y: -outward.y * amount,
  };
}

/**
 * Extend the painted path a few pixels beneath each node. Routing continues
 * to use the exact measured boundary; this presentation-only overlap hides
 * anti-aliasing seams from theme borders and heavier selected-edge strokes.
 */
export function routePathWithEndpointOverlap(route, amount = EDGE_ENDPOINT_OVERLAP) {
  if (!route?.segments?.length || !SIDE_VECTORS[route.side] || !SIDE_VECTORS[route.targetSide]) {
    return route?.path || '';
  }
  const overlap = Number.isFinite(amount) ? Math.max(0, amount) : EDGE_ENDPOINT_OVERLAP;
  if (overlap === 0) return route.path;

  const first = route.segments[0];
  const last = route.segments[route.segments.length - 1];
  const sourceDelta = inwardDelta(route.side, overlap);
  const targetDelta = inwardDelta(route.targetSide, overlap);
  // Extend only beneath the node faces. Moving the curve's control points
  // would also bend its visible span and could introduce unchecked crossings.
  const sourceInside = { x: first.from.x + sourceDelta.x, y: first.from.y + sourceDelta.y };
  const targetInside = { x: last.to.x + targetDelta.x, y: last.to.y + targetDelta.y };
  return `M ${sourceInside.x} ${sourceInside.y} L ${first.from.x} ${first.from.y} ${route.segments.map(segmentPath).join(' ')} L ${targetInside.x} ${targetInside.y}`;
}

function routeFromSegments({
  side,
  targetSide,
  segments,
  points,
  bends = 0,
  kind = 'smooth',
  cornerRadius = DEFAULT_CORNER_RADIUS,
}) {
  const anchors = {
    source: segments[0].from,
    target: segments[segments.length - 1].to,
  };
  const flattened = flattenSegments(segments);
  const xs = flattened.map((point) => point.x);
  const ys = flattened.map((point) => point.y);

  return {
    side,
    targetSide,
    from: anchors.source,
    to: anchors.target,
    anchors,
    segments,
    points,
    flattened,
    bends,
    kind,
    cornerRadius,
    bounds: {
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    },
    path: `M ${anchors.source.x} ${anchors.source.y} ${segments.map(segmentPath).join(' ')}`,
  };
}

export function routeBoundaryCurve(fromRect, toRect, options = {}) {
  const side = SIDES.includes(options.side)
    ? options.side
    : connectionSide(fromRect, toRect);
  const targetSide = oppositeSide(side);
  const from = rectSideAnchor(fromRect, side);
  const to = rectSideAnchor(toRect, targetSide);
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  let control1;
  let control2;
  if (side === 'left' || side === 'right') {
    const bend = Math.max(32, Math.min(Math.abs(dx) * 0.5, 220));
    const direction = side === 'left' ? -1 : 1;
    control1 = { x: from.x + bend * direction, y: from.y };
    control2 = { x: to.x - bend * direction, y: to.y };
  } else {
    const bend = Math.max(32, Math.min(Math.abs(dy) * 0.5, 220));
    const direction = side === 'top' ? -1 : 1;
    control1 = { x: from.x, y: from.y + bend * direction };
    control2 = { x: to.x, y: to.y - bend * direction };
  }

  const route = routeFromSegments({
    side,
    targetSide,
    segments: [cubicSegment(from, control1, control2, to)],
    points: [from, to],
  });
  return { ...route, control1, control2 };
}

function distancePointToLine(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared < EPSILON) return Math.hypot(point.x - start.x, point.y - start.y);
  const area = Math.abs(
    dy * point.x - dx * point.y + end.x * start.y - end.y * start.x
  );
  return area / Math.sqrt(lengthSquared);
}

function flattenCubic(segment, tolerance = 1.25, depth = 0) {
  const flatness = Math.max(
    distancePointToLine(segment.control1, segment.from, segment.to),
    distancePointToLine(segment.control2, segment.from, segment.to)
  );
  if (flatness <= tolerance || depth >= 10) return [segment.from, segment.to];

  const p01 = midpoint(segment.from, segment.control1);
  const p12 = midpoint(segment.control1, segment.control2);
  const p23 = midpoint(segment.control2, segment.to);
  const p012 = midpoint(p01, p12);
  const p123 = midpoint(p12, p23);
  const split = midpoint(p012, p123);

  const left = flattenCubic(
    cubicSegment(segment.from, p01, p012, split),
    tolerance,
    depth + 1
  );
  const right = flattenCubic(
    cubicSegment(split, p123, p23, segment.to),
    tolerance,
    depth + 1
  );
  return [...left.slice(0, -1), ...right];
}

function flattenSegments(segments, tolerance = 1.25) {
  const points = [];
  segments.forEach((segment, index) => {
    const flattened = flattenCubic(segment, tolerance);
    points.push(...(index === 0 ? flattened : flattened.slice(1)));
  });
  return points;
}

export function flattenRoute(route, tolerance = 1.25) {
  if (!route) return [];
  if (Array.isArray(route.flattened) && tolerance === 1.25) return route.flattened;
  if (Array.isArray(route.segments)) return flattenSegments(route.segments, tolerance);
  if (Array.isArray(route.points)) return route.points;
  return [];
}

function midpoint(left, right) {
  return {
    x: (left.x + right.x) / 2,
    y: (left.y + right.y) / 2,
  };
}

function pointDistance(left, right) {
  return Math.hypot(right.x - left.x, right.y - left.y);
}

function pointStrictlyInsideRect(point, rect) {
  return point.x > rect.x + EPSILON
    && point.x < rect.x + rect.width - EPSILON
    && point.y > rect.y + EPSILON
    && point.y < rect.y + rect.height - EPSILON;
}

function lineIntersectsRectInterior(start, end, rect) {
  // Most visibility-graph segments sit entirely to one side of an obstacle.
  // Reject those before allocating and evaluating the clipping planes.
  if (Math.max(start.x, end.x) <= rect.x
    || Math.min(start.x, end.x) >= rect.x + rect.width
    || Math.max(start.y, end.y) <= rect.y
    || Math.min(start.y, end.y) >= rect.y + rect.height) return false;
  if (pointStrictlyInsideRect(start, rect) || pointStrictlyInsideRect(end, rect)) {
    return true;
  }

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  let minimum = 0;
  let maximum = 1;
  const clips = [
    [-dx, start.x - rect.x],
    [dx, rect.x + rect.width - start.x],
    [-dy, start.y - rect.y],
    [dy, rect.y + rect.height - start.y],
  ];

  for (const [p, q] of clips) {
    if (Math.abs(p) < EPSILON) {
      if (q < 0) return false;
      continue;
    }
    const ratio = q / p;
    if (p < 0) minimum = Math.max(minimum, ratio);
    else maximum = Math.min(maximum, ratio);
    if (minimum > maximum) return false;
  }

  if (maximum - minimum <= EPSILON) return false;
  const sample = {
    x: start.x + dx * ((minimum + maximum) / 2),
    y: start.y + dy * ((minimum + maximum) / 2),
  };
  return pointStrictlyInsideRect(sample, rect);
}

function boundsExcludeRect(bounds, rect) {
  return Boolean(
    bounds && (
      bounds.x + bounds.width <= rect.x
      || rect.x + rect.width <= bounds.x
      || bounds.y + bounds.height <= rect.y
      || rect.y + rect.height <= bounds.y
    )
  );
}

function boundsOverlapWithPadding(left, right, padding = 0) {
  if (!left || !right) return true;
  const safePadding = Math.max(0, Number.isFinite(padding) ? padding : 0);
  return !(
    left.x + left.width + safePadding < right.x
    || right.x + right.width + safePadding < left.x
    || left.y + left.height + safePadding < right.y
    || right.y + right.height + safePadding < left.y
  );
}

function polylineIntersectsRect(points, bounds, rect) {
  if (boundsExcludeRect(bounds, rect)) return false;
  for (let index = 1; index < points.length; index += 1) {
    if (lineIntersectsRectInterior(points[index - 1], points[index], rect)) return true;
  }
  return false;
}

export function routeIntersectsRect(route, rect) {
  if (boundsExcludeRect(route?.bounds, rect)) return false;
  return polylineIntersectsRect(flattenRoute(route), route?.bounds, rect);
}

function crossProduct(a, b, c) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function pointOnSegment(point, start, end) {
  return Math.abs(crossProduct(start, end, point)) < EPSILON
    && point.x >= Math.min(start.x, end.x) - EPSILON
    && point.x <= Math.max(start.x, end.x) + EPSILON
    && point.y >= Math.min(start.y, end.y) - EPSILON
    && point.y <= Math.max(start.y, end.y) + EPSILON;
}

function segmentIntersection(leftStart, leftEnd, rightStart, rightEnd) {
  const leftA = crossProduct(leftStart, leftEnd, rightStart);
  const leftB = crossProduct(leftStart, leftEnd, rightEnd);
  const rightA = crossProduct(rightStart, rightEnd, leftStart);
  const rightB = crossProduct(rightStart, rightEnd, leftEnd);
  const denominator = (
    (leftStart.x - leftEnd.x) * (rightStart.y - rightEnd.y)
    - (leftStart.y - leftEnd.y) * (rightStart.x - rightEnd.x)
  );

  if (Math.abs(denominator) > EPSILON) {
    const t = (
      (leftStart.x - rightStart.x) * (rightStart.y - rightEnd.y)
      - (leftStart.y - rightStart.y) * (rightStart.x - rightEnd.x)
    ) / denominator;
    const u = -(
      (leftStart.x - leftEnd.x) * (leftStart.y - rightStart.y)
      - (leftStart.y - leftEnd.y) * (leftStart.x - rightStart.x)
    ) / denominator;
    if (t >= -EPSILON && t <= 1 + EPSILON && u >= -EPSILON && u <= 1 + EPSILON) {
      return {
        x: leftStart.x + t * (leftEnd.x - leftStart.x),
        y: leftStart.y + t * (leftEnd.y - leftStart.y),
      };
    }
    return null;
  }

  if (Math.abs(leftA) > EPSILON || Math.abs(leftB) > EPSILON
    || Math.abs(rightA) > EPSILON || Math.abs(rightB) > EPSILON) {
    return null;
  }
  const candidates = [leftStart, leftEnd, rightStart, rightEnd]
    .filter((point) => pointOnSegment(point, leftStart, leftEnd)
      && pointOnSegment(point, rightStart, rightEnd))
    .sort(comparePoints);
  return candidates[0] || null;
}

function sharedAnchors(left, right) {
  const leftAnchors = [left?.anchors?.source, left?.anchors?.target].filter(Boolean);
  const rightAnchors = [right?.anchors?.source, right?.anchors?.target].filter(Boolean);
  return leftAnchors.filter((leftAnchor) => rightAnchors.some(
    (rightAnchor) => pointDistance(leftAnchor, rightAnchor) < 0.5
  ));
}

export function routeCrossesRoute(left, right, sharedEndpointRadius = 18) {
  if (
    left?.bounds
    && right?.bounds
    && (
      left.bounds.x + left.bounds.width < right.bounds.x
      || right.bounds.x + right.bounds.width < left.bounds.x
      || left.bounds.y + left.bounds.height < right.bounds.y
      || right.bounds.y + right.bounds.height < left.bounds.y
    )
  ) {
    return false;
  }
  const leftPoints = flattenRoute(left);
  const rightPoints = flattenRoute(right);
  const ignoredAnchors = sharedAnchors(left, right);

  for (let leftIndex = 1; leftIndex < leftPoints.length; leftIndex += 1) {
    for (let rightIndex = 1; rightIndex < rightPoints.length; rightIndex += 1) {
      const intersection = segmentIntersection(
        leftPoints[leftIndex - 1],
        leftPoints[leftIndex],
        rightPoints[rightIndex - 1],
        rightPoints[rightIndex]
      );
      if (!intersection) continue;
      const isSharedEndpoint = ignoredAnchors.some(
        (anchor) => pointDistance(anchor, intersection) <= sharedEndpointRadius
      );
      if (!isSharedEndpoint) return true;
    }
  }
  return false;
}

function comparePoints(left, right) {
  if (left.x !== right.x) return left.x - right.x;
  return left.y - right.y;
}

function rectCorners(rect) {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

function routeLength(points) {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    length += pointDistance(points[index - 1], points[index]);
  }
  return length;
}

function edgeDetourBudget(route) {
  const directLength = routeLength(flattenRoute(route));
  return directLength + Math.max(
    MIN_EDGE_DETOUR_ALLOWANCE,
    directLength * (MAX_EDGE_DETOUR_RATIO - 1)
  );
}

function dedupePoints(points) {
  const seen = new Set();
  return points.filter((point) => {
    const key = `${point.x.toFixed(4)}:${point.y.toFixed(4)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function routingPerimeterPoints(rects, edgeObstacles, sourceLead, targetLead) {
  const edgePoints = edgeObstacles.flatMap((route) => flattenRoute(route));
  const allPoints = [
    sourceLead,
    targetLead,
    ...rects.flatMap(rectCorners),
    ...edgePoints,
  ];
  if (allPoints.length === 0) return [];
  const margin = 36;
  const minX = Math.min(...allPoints.map((point) => point.x)) - margin;
  const maxX = Math.max(...allPoints.map((point) => point.x)) + margin;
  const minY = Math.min(...allPoints.map((point) => point.y)) - margin;
  const maxY = Math.max(...allPoints.map((point) => point.y)) + margin;
  return [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
    { x: sourceLead.x, y: minY },
    { x: sourceLead.x, y: maxY },
    { x: targetLead.x, y: minY },
    { x: targetLead.x, y: maxY },
    { x: minX, y: sourceLead.y },
    { x: maxX, y: sourceLead.y },
    { x: minX, y: targetLead.y },
    { x: maxX, y: targetLead.y },
  ];
}

function edgeLaneWaypoints(edgeObstacles, clearance = DEFAULT_EDGE_LANE_CLEARANCE) {
  return edgeObstacles.flatMap((route) => {
    const points = flattenRoute(route);
    if (points.length < 2) return [];
    const waypoints = [];
    const pointIndexes = new Set([0, points.length - 1]);
    for (const coordinate of ['x', 'y']) {
      let minimumIndex = 0;
      let maximumIndex = 0;
      points.forEach((point, index) => {
        if (point[coordinate] < points[minimumIndex][coordinate]) minimumIndex = index;
        if (point[coordinate] > points[maximumIndex][coordinate]) maximumIndex = index;
      });
      pointIndexes.add(minimumIndex);
      pointIndexes.add(maximumIndex);
    }

    [...pointIndexes].sort((left, right) => left - right).forEach((index) => {
      const point = points[index];
      const previous = points[Math.max(0, index - 1)];
      const next = points[Math.min(points.length - 1, index + 1)];
      const dx = next.x - previous.x;
      const dy = next.y - previous.y;
      const length = Math.hypot(dx, dy);
      if (length < EPSILON) return;
      const normal = {
        x: -dy / length,
        y: dx / length,
      };
      waypoints.push(
        {
          x: point.x + normal.x * clearance,
          y: point.y + normal.y * clearance,
        },
        {
          x: point.x - normal.x * clearance,
          y: point.y - normal.y * clearance,
        }
      );
    });

    const start = points[0];
    const startNext = points[1];
    const startLength = Math.max(pointDistance(start, startNext), EPSILON);
    waypoints.push({
      x: start.x - ((startNext.x - start.x) / startLength) * clearance,
      y: start.y - ((startNext.y - start.y) / startLength) * clearance,
    });

    const end = points[points.length - 1];
    const endPrevious = points[points.length - 2];
    const endLength = Math.max(pointDistance(endPrevious, end), EPSILON);
    waypoints.push({
      x: end.x + ((end.x - endPrevious.x) / endLength) * clearance,
      y: end.y + ((end.y - endPrevious.y) / endLength) * clearance,
    });

    return waypoints;
  });
}

function visibleSegment(start, end, obstacles) {
  return !obstacles.some((rect) => lineIntersectsRectInterior(start, end, rect));
}

function segmentCrossingCount(start, end, edgeObstacles) {
  if (edgeObstacles.length === 0) return 0;
  const route = routeFromSegments({
    side: 'right',
    targetSide: 'left',
    segments: [lineAsCubic(start, end)],
    points: [start, end],
  });
  return edgeObstacles.reduce(
    (count, obstacle) => count + (routeCrossesRoute(route, obstacle, 12) ? 1 : 0),
    0
  );
}

function shortestVisiblePath(start, target, obstacles, edgeObstacles) {
  if (pointDistance(start, target) < EPSILON) return [start, target];
  const expandedForWaypoints = obstacles.map((rect) => inflateRect(rect, DEFAULT_CORNER_RADIUS + 2));
  const points = dedupePoints([
    start,
    target,
    ...expandedForWaypoints.flatMap(rectCorners),
    ...edgeLaneWaypoints(edgeObstacles),
    ...routingPerimeterPoints(expandedForWaypoints, edgeObstacles, start, target),
  ]);
  const startIndex = points.findIndex((point) => point === start);
  const targetIndex = points.findIndex((point) => point === target);
  const distances = points.map(() => Infinity);
  const previous = points.map(() => -1);
  const visited = points.map(() => false);
  distances[startIndex] = 0;

  for (let iteration = 0; iteration < points.length; iteration += 1) {
    let current = -1;
    for (let index = 0; index < points.length; index += 1) {
      if (visited[index]) continue;
      if (
        current === -1
        || distances[index] < distances[current] - EPSILON
        || (
          Math.abs(distances[index] - distances[current]) < EPSILON
          && comparePoints(points[index], points[current]) < 0
        )
      ) {
        current = index;
      }
    }
    if (current === -1 || distances[current] === Infinity) break;
    if (current === targetIndex) break;
    visited[current] = true;

    for (let next = 0; next < points.length; next += 1) {
      if (next === current || visited[next]) continue;
      const distance = pointDistance(points[current], points[next]);
      // Crossing penalties are nonnegative. Skip expensive visibility and
      // curve tests when even a clear segment cannot improve this distance.
      // Retain ties so the deterministic predecessor ordering is unchanged.
      if (distances[current] + distance > distances[next] + EPSILON) continue;
      if (!visibleSegment(points[current], points[next], obstacles)) continue;
      const crossings = segmentCrossingCount(points[current], points[next], edgeObstacles);
      const candidate = distances[current] + distance + crossings * 100000;
      if (
        candidate < distances[next] - EPSILON
        || (
          Math.abs(candidate - distances[next]) < EPSILON
          && (previous[next] === -1 || current < previous[next])
        )
      ) {
        distances[next] = candidate;
        previous[next] = current;
      }
    }
  }

  if (distances[targetIndex] === Infinity) return null;
  const path = [];
  let cursor = targetIndex;
  while (cursor !== -1) {
    path.push(points[cursor]);
    cursor = previous[cursor];
  }
  return path.reverse();
}

function simplifyPolyline(points, obstacles, edgeObstacles = []) {
  if (points.length <= 2) return points;
  const simplified = [points[0]];
  let cursor = 0;
  while (cursor < points.length - 1) {
    let next = points.length - 1;
    while (
      next > cursor + 1
      && (
        !visibleSegment(points[cursor], points[next], obstacles)
        || segmentCrossingCount(points[cursor], points[next], edgeObstacles) > 0
      )
    ) {
      next -= 1;
    }
    simplified.push(points[next]);
    cursor = next;
  }
  return simplified;
}

function smoothPolyline(points, radius = DEFAULT_CORNER_RADIUS) {
  if (points.length < 2) return [];
  if (points.length === 2) return [lineAsCubic(points[0], points[1])];

  const segments = [];
  let cursor = points[0];
  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const corner = points[index];
    const next = points[index + 1];
    const beforeDistance = Math.min(radius, pointDistance(previous, corner) / 3);
    const afterDistance = Math.min(radius, pointDistance(corner, next) / 3);
    const beforeRatio = beforeDistance / Math.max(pointDistance(previous, corner), EPSILON);
    const afterRatio = afterDistance / Math.max(pointDistance(corner, next), EPSILON);
    const before = {
      x: corner.x + (previous.x - corner.x) * beforeRatio,
      y: corner.y + (previous.y - corner.y) * beforeRatio,
    };
    const after = {
      x: corner.x + (next.x - corner.x) * afterRatio,
      y: corner.y + (next.y - corner.y) * afterRatio,
    };
    if (pointDistance(cursor, before) > EPSILON) segments.push(lineAsCubic(cursor, before));
    segments.push(cubicSegment(
      before,
      {
        x: before.x + ((corner.x - before.x) * 2) / 3,
        y: before.y + ((corner.y - before.y) * 2) / 3,
      },
      {
        x: after.x + ((corner.x - after.x) * 2) / 3,
        y: after.y + ((corner.y - after.y) * 2) / 3,
      },
      after
    ));
    cursor = after;
  }
  if (pointDistance(cursor, points[points.length - 1]) > EPSILON) {
    segments.push(lineAsCubic(cursor, points[points.length - 1]));
  }
  return segments;
}

function simplifyOrthogonalPoints(points) {
  const deduped = dedupePoints(points);
  if (deduped.length <= 2) return deduped;
  const simplified = [deduped[0]];
  for (let index = 1; index < deduped.length - 1; index += 1) {
    const previous = simplified[simplified.length - 1];
    const current = deduped[index];
    const next = deduped[index + 1];
    const isHorizontal = Math.abs(previous.y - current.y) < EPSILON
      && Math.abs(current.y - next.y) < EPSILON;
    const isVertical = Math.abs(previous.x - current.x) < EPSILON
      && Math.abs(current.x - next.x) < EPSILON;
    if (!isHorizontal && !isVertical) simplified.push(current);
  }
  simplified.push(deduped[deduped.length - 1]);
  return simplified;
}

function isHorizontalSide(side) {
  return side === 'left' || side === 'right';
}

function visiblePolyline(points, obstacles) {
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const isOrthogonal = (
      Math.abs(previous.x - current.x) < EPSILON
      || Math.abs(previous.y - current.y) < EPSILON
    );
    if (!isOrthogonal || !visibleSegment(previous, current, obstacles)) return false;
  }
  return true;
}

function routeOrthogonalPoints({
  points,
  side,
  targetSide,
  kind = 'rounded',
  cornerRadius = ORTHOGONAL_CORNER_RADIUS,
}) {
  const simplified = simplifyOrthogonalPoints(points);
  return routeFromSegments({
    side,
    targetSide,
    segments: cornerRadius > 0
      ? smoothPolyline(simplified, cornerRadius)
      : simplified.slice(1).map((point, index) => lineAsCubic(simplified[index], point)),
    points: simplified,
    bends: Math.max(0, simplified.length - 2),
    kind,
    cornerRadius,
  });
}

// User-authored elbows retain the selected corner style and boundary
// anchors. These controls are separate from the automatic router's cache.
export function routeManualConnector(points, route) {
  if (!Array.isArray(points) || points.length < 2 || points.length > 64
    || points.some((point) => !Number.isFinite(point?.x) || !Number.isFinite(point?.y))) return null;
  if (!visiblePolyline(points, [])) return null;
  const sourceVector = SIDE_VECTORS[route.side];
  const targetVector = SIDE_VECTORS[route.targetSide];
  if (!sourceVector || !targetVector) return null;
  const first = points[0], second = points[1];
  const last = points[points.length - 1], beforeLast = points[points.length - 2];
  if ((second.x - first.x) * sourceVector.x + (second.y - first.y) * sourceVector.y < -EPSILON
    || (beforeLast.x - last.x) * targetVector.x + (beforeLast.y - last.y) * targetVector.y < -EPSILON) return null;
  return {
    ...route,
    ...routeOrthogonalPoints({ points, side: route.side, targetSide: route.targetSide, kind: route.kind, cornerRadius: route.cornerRadius ?? (route.kind === 'branch' ? 0 : ORTHOGONAL_CORNER_RADIUS) }),
    manual: true,
  };
}

function orthogonalLaneValues(sourceLead, targetLead, sourceSide, targetSide, obstacles, preferredLane) {
  const horizontal = isHorizontalSide(sourceSide) && isHorizontalSide(targetSide);
  const sourceValue = horizontal ? sourceLead.x : sourceLead.y;
  const targetValue = horizontal ? targetLead.x : targetLead.y;
  const minimum = Math.min(sourceValue, targetValue) - 160;
  const maximum = Math.max(sourceValue, targetValue) + 160;
  const values = [
    ...(Number.isFinite(preferredLane) ? [preferredLane] : []),
    (sourceValue + targetValue) / 2,
    sourceValue,
    targetValue,
  ];
  obstacles.forEach((rect) => {
    if (horizontal) values.push(rect.x, rect.x + rect.width);
    else values.push(rect.y, rect.y + rect.height);
  });
  return [...new Set(values.map((value) => Math.round(value * 1000) / 1000))]
    .filter((value) => value >= minimum && value <= maximum)
    .sort((left, right) => (
      Math.abs(left - (sourceValue + targetValue) / 2)
      - Math.abs(right - (sourceValue + targetValue) / 2)
      || left - right
    ));
}

function orthogonalPointCandidates({
  source,
  target,
  sourceLead,
  targetLead,
  sourceSide,
  targetSide,
  obstacles,
  preferredLane,
}) {
  const sourceHorizontal = isHorizontalSide(sourceSide);
  const targetHorizontal = isHorizontalSide(targetSide);
  if (sourceHorizontal !== targetHorizontal) {
    const elbow = sourceHorizontal
      ? { x: targetLead.x, y: sourceLead.y }
      : { x: sourceLead.x, y: targetLead.y };
    return [[source, sourceLead, elbow, targetLead, target]];
  }

  return orthogonalLaneValues(
    sourceLead,
    targetLead,
    sourceSide,
    targetSide,
    obstacles,
    preferredLane
  ).map((lane) => (
    sourceHorizontal
      ? [
          source,
          sourceLead,
          { x: lane, y: sourceLead.y },
          { x: lane, y: targetLead.y },
          targetLead,
          target,
        ]
      : [
          source,
          sourceLead,
          { x: sourceLead.x, y: lane },
          { x: targetLead.x, y: lane },
          targetLead,
          target,
        ]
  ));
}

function routeRoundedEdge({
  sourceRect,
  targetRect,
  nodeObstacles = [],
  edgeObstacles = [],
  clearance = DEFAULT_CLEARANCE,
  preferredLane = null,
}) {
  const obstacles = nodeObstacles
    .map((obstacle) => inflateRect(obstacle.rect || obstacle, clearance))
    .sort((left, right) => comparePoints(left, right));
  const preferredSide = connectionSide(sourceRect, targetRect);
  const candidates = [];

  for (const sourceSide of orderedSides(preferredSide)) {
    for (const targetSide of orderedSides(oppositeSide(preferredSide))) {
      const source = rectSideAnchor(sourceRect, sourceSide);
      const target = rectSideAnchor(targetRect, targetSide);
      const sourceVector = SIDE_VECTORS[sourceSide];
      const targetVector = SIDE_VECTORS[targetSide];
      const sourceLead = {
        x: source.x + sourceVector.x * DEFAULT_LEAD,
        y: source.y + sourceVector.y * DEFAULT_LEAD,
      };
      const targetLead = {
        x: target.x + targetVector.x * DEFAULT_LEAD,
        y: target.y + targetVector.y * DEFAULT_LEAD,
      };
      const pointCandidates = orthogonalPointCandidates({
        source,
        target,
        sourceLead,
        targetLead,
        sourceSide,
        targetSide,
        obstacles,
        preferredLane,
      });
      pointCandidates.forEach((points) => {
        const simplified = simplifyOrthogonalPoints(points);
        if (!visiblePolyline(simplified, obstacles)) return;
        const route = routeOrthogonalPoints({
          points: simplified,
          side: sourceSide,
          targetSide,
        });
        const cost = scoreRoute(route, {
          nodeObstacles: obstacles,
          edgeObstacles,
          // Rounded elbows follow current geometry rather than drag history.
          previousRoute: null,
          preferredSide,
        });
        // Equal-length elbows used to be selected by their SVG path string.
        // That favoured different lanes on opposite sides and could collapse
        // separate rounded connections onto the parent's lead. Prefer the
        // midpoint geometrically; obstacle and port penalties still dominate.
        if (isHorizontalSide(sourceSide) === isHorizontalSide(targetSide)) {
          const horizontal = isHorizontalSide(sourceSide);
          const lanePoint = points[2];
          const midpoint = horizontal
            ? (sourceLead.x + targetLead.x) / 2
            : (sourceLead.y + targetLead.y) / 2;
          cost.total += Math.abs((horizontal ? lanePoint.x : lanePoint.y) - (Number.isFinite(preferredLane) && sourceSide === preferredSide ? preferredLane : midpoint));
        }
        candidates.push({ route, cost });
      });
    }
  }

  const clearCandidates = candidates.filter(({ cost }) => cost.nodeIntersections === 0);
  if (clearCandidates.length > 0) {
    const selected = clearCandidates.sort(compareCandidates)[0];
    return {
      ...selected.route,
      sourceRect,
      targetRect,
      cost: selected.cost,
    };
  }

  const fallback = routeEdge({
    sourceRect,
    targetRect,
    nodeObstacles,
    edgeObstacles,
    previousRoute: null,
    style: DEFAULT_CONNECTION_STYLE_ID,
    clearance,
  });
  return { ...fallback, routingFallback: 'orthogonal-unavailable' };
}

function orderedSides(preferred) {
  const start = SIDES.indexOf(preferred);
  return SIDES.map((_, index) => SIDES[(start + index) % SIDES.length]);
}

function retargetPreviousRoute(previousRoute, sourceRect, targetRect) {
  if (!previousRoute) return null;
  const side = SIDES.includes(previousRoute.side)
    ? previousRoute.side
    : connectionSide(sourceRect, targetRect);
  const targetSide = SIDES.includes(previousRoute.targetSide)
    ? previousRoute.targetSide
    : oppositeSide(side);
  const source = rectSideAnchor(sourceRect, side);
  const target = rectSideAnchor(targetRect, targetSide);
  const previousPoints = Array.isArray(previousRoute.points)
    ? previousRoute.points
    : [];

  if (previousPoints.length <= 2) {
    return routeBoundaryCurve(sourceRect, targetRect, { side });
  }

  const previousSource = previousPoints[0];
  const previousTarget = previousPoints[previousPoints.length - 1];
  const sourceDelta = {
    x: source.x - previousSource.x,
    y: source.y - previousSource.y,
  };
  const targetDelta = {
    x: target.x - previousTarget.x,
    y: target.y - previousTarget.y,
  };
  const points = previousPoints.map((point, index) => {
    if (index === 0) return source;
    if (index === previousPoints.length - 1) return target;
    const ratio = index / (previousPoints.length - 1);
    return {
      x: point.x + sourceDelta.x * (1 - ratio) + targetDelta.x * ratio,
      y: point.y + sourceDelta.y * (1 - ratio) + targetDelta.y * ratio,
    };
  });
  const cornerRadius = Number.isFinite(previousRoute.cornerRadius)
    ? previousRoute.cornerRadius
    : DEFAULT_CORNER_RADIUS;
  const segments = cornerRadius > 0
    ? smoothPolyline(points, cornerRadius)
    : points.slice(1).map((point, index) => lineAsCubic(points[index], point));

  return routeFromSegments({
    side,
    targetSide,
    segments,
    points,
    bends: Math.max(0, points.length - 2),
    kind: previousRoute.kind || 'obstacle',
    cornerRadius,
  });
}

function routeSimilarity(route, previousRoute) {
  if (!previousRoute) return 0;
  const routePoints = flattenRoute(route);
  const previousPoints = flattenRoute(previousRoute);
  if (routePoints.length === 0 || previousPoints.length === 0) return 0;
  const samples = 8;
  let distance = 0;
  for (let index = 0; index <= samples; index += 1) {
    const routePoint = routePoints[Math.round((routePoints.length - 1) * index / samples)];
    const previousPoint = previousPoints[
      Math.round((previousPoints.length - 1) * index / samples)
    ];
    distance += pointDistance(routePoint, previousPoint);
  }
  return distance;
}

function scoreRoute(route, {
  nodeObstacles,
  edgeObstacles,
  previousRoute,
  preferredSide,
}) {
  // Flatten once per candidate. Re-flattening the same cubic for every node
  // turned an otherwise linear visible-route pass into seconds of work in a
  // 70-node overscan window.
  const flattenedRoute = flattenRoute(route);
  const nodeIntersections = nodeObstacles.reduce(
    (count, rect) => count + (
      polylineIntersectsRect(flattenedRoute, route?.bounds, rect) ? 1 : 0
    ),
    0
  );
  const edgeCrossings = edgeObstacles.reduce(
    (count, edge) => count + (routeCrossesRoute(route, edge) ? 1 : 0),
    0
  );
  const length = routeLength(flattenedRoute);
  const preferredTargetSide = oppositeSide(preferredSide);
  const sourcePortPenalty = route.side === preferredSide ? 0 : 1;
  const targetPortPenalty = route.targetSide === preferredTargetSide ? 0 : 1;
  const portPenalty = sourcePortPenalty + targetPortPenalty;
  const similarity = routeSimilarity(route, previousRoute);
  const smoothnessPenalty = route.kind === 'obstacle'
    ? Math.max(0, DEFAULT_CORNER_RADIUS - (route.cornerRadius || 0))
    : 0;
  return {
    nodeIntersections,
    edgeCrossings,
    sourcePortPenalty,
    targetPortPenalty,
    portPenalty,
    similarity,
    length,
    bends: route.bends,
    smoothnessPenalty,
    total: nodeIntersections * 1e12
      + edgeCrossings * 1e9
      + portPenalty * 1e6
      + smoothnessPenalty * 10000
      + similarity * 10
      + length
      + route.bends * 24,
  };
}

function compareCandidates(left, right) {
  if (left.cost.total !== right.cost.total) return left.cost.total - right.cost.total;
  if (left.route.path !== right.route.path) return left.route.path.localeCompare(right.route.path);
  return 0;
}

/**
 * Deterministically route one connection around inflated node rectangles and
 * previously routed connections. Routes are derived display state and should
 * never be persisted in the shared document.
 */
export function routeEdge({
  sourceRect,
  targetRect,
  nodeObstacles = [],
  edgeObstacles = [],
  previousRoute = null,
  style = 'smooth',
  preferredRoute = null,
  preferredLane = null,
  clearance = DEFAULT_CLEARANCE,
} = {}) {
  if (!sourceRect || !targetRect) {
    throw new TypeError('routeEdge requires sourceRect and targetRect');
  }
  const normalizedStyle = normalizeConnectionStyleId(style);
  if (normalizedStyle === 'rounded' || normalizedStyle === 'branch') {
    return routeRoundedEdge({
      sourceRect,
      targetRect,
      nodeObstacles,
      edgeObstacles,
      previousRoute,
      clearance,
      preferredLane,
    });
  }

  const obstacles = nodeObstacles
    .map((obstacle) => inflateRect(obstacle.rect || obstacle, clearance))
    .sort((left, right) => (
      left.x - right.x
      || left.y - right.y
      || left.width - right.width
      || left.height - right.height
    ));
  const preferredSide = connectionSide(sourceRect, targetRect);
  const candidates = [];
  const fallback = preferredRoute || routeBoundaryCurve(sourceRect, targetRect, { side: preferredSide });
  const fallbackCost = scoreRoute(fallback, {
    nodeObstacles: obstacles,
    edgeObstacles,
    previousRoute,
    preferredSide,
  });
  const retargetedPreviousRoute = retargetPreviousRoute(
    previousRoute,
    sourceRect,
    targetRect
  );
  const retargetedPrevious = retargetedPreviousRoute
    ? {
        route: retargetedPreviousRoute,
        cost: scoreRoute(retargetedPreviousRoute, {
          nodeObstacles: obstacles,
          edgeObstacles,
          previousRoute,
          preferredSide,
        }),
      }
    : null;
  const maximumEdgeDetourLength = edgeDetourBudget(fallback);
  const requiresNodeDetour = fallbackCost.nodeIntersections > 0;
  const isAcceptableDetour = (route) => (
    requiresNodeDetour
    || routeLength(flattenRoute(route)) <= maximumEdgeDetourLength + EPSILON
  );
  const chooseWithHysteresis = (next) => {
    if (!retargetedPrevious) return next;
    const nextUsesPreferredPorts = (
      next.route.side === preferredSide
      && next.route.targetSide === oppositeSide(preferredSide)
    );
    const previousUsesPreferredPorts = (
      retargetedPrevious.route.side === preferredSide
      && retargetedPrevious.route.targetSide === oppositeSide(preferredSide)
    );
    // Hysteresis should preserve the shape of a route, not a stale attachment
    // side after the geometry clearly favours another face of the node.
    if (nextUsesPreferredPorts && !previousUsesPreferredPorts) return next;
    const previousCost = retargetedPrevious.cost;
    const previousIsValid = (
      previousCost.nodeIntersections === 0
      && previousCost.edgeCrossings === 0
      && isAcceptableDetour(retargetedPrevious.route)
    );
    if (!previousIsValid) return next;
    const nextIsValid = (
      next.cost.nodeIntersections === 0
      && next.cost.edgeCrossings === 0
    );
    if (!nextIsValid) return retargetedPrevious;
    const previousIsCompetitive = (
      previousCost.length
      <= next.cost.length * ROUTE_HYSTERESIS_LENGTH_RATIO
        + ROUTE_HYSTERESIS_LENGTH_ALLOWANCE
    );
    return previousIsCompetitive ? retargetedPrevious : next;
  };
  if (fallbackCost.nodeIntersections === 0 && fallbackCost.edgeCrossings === 0) {
    const selected = chooseWithHysteresis({ route: fallback, cost: fallbackCost });
    return {
      ...selected.route,
      sourceRect,
      targetRect,
      cost: selected.cost,
    };
  }
  candidates.push(fallback);
  if (retargetedPreviousRoute) candidates.push(retargetedPreviousRoute);
  const fallbackNodeObstacles = obstacles.filter(
    (rect) => routeIntersectsRect(fallback, rect)
  );
  const fallbackCrossedEdges = edgeObstacles.filter(
    (edge) => routeCrossesRoute(fallback, edge)
  );

  for (const sourceSide of orderedSides(preferredSide)) {
    for (const targetSide of orderedSides(oppositeSide(sourceSide))) {
      const source = rectSideAnchor(sourceRect, sourceSide);
      const target = rectSideAnchor(targetRect, targetSide);
      const sourceVector = SIDE_VECTORS[sourceSide];
      const targetVector = SIDE_VECTORS[targetSide];
      const sourceLead = {
        x: source.x + sourceVector.x * DEFAULT_LEAD,
        y: source.y + sourceVector.y * DEFAULT_LEAD,
      };
      const targetLead = {
        x: target.x + targetVector.x * DEFAULT_LEAD,
        y: target.y + targetVector.y * DEFAULT_LEAD,
      };
      const isPreferredPortPair = (
        sourceSide === preferredSide
        && targetSide === oppositeSide(preferredSide)
      );
      // The common path only needs obstacles that invalidate the direct
      // curve. Validate the result against the complete set below; if the
      // local detour meets another obstacle, alternate port pairs retain the
      // comprehensive graph. This keeps dense sibling fans responsive.
      const navigationNodeObstacles = isPreferredPortPair
        ? fallbackNodeObstacles
        : obstacles;
      // Edge-lane waypoints grow the visibility graph quadratically. Routes
      // that do not cross the natural boundary curve cannot help resolve its
      // crossings, and adding all of them made viewport zoom progressively
      // slower as more already-routed edges entered overscan. Keep the search
      // graph local to the lanes that actually block the natural route; final
      // candidate scoring still checks every visible edge.
      const navigationEdgeObstacles = fallbackCrossedEdges;
      const navigationObstacles = [
        ...navigationNodeObstacles,
        sourceRect,
        targetRect,
      ];
      if (
        !visibleSegment(source, sourceLead, navigationObstacles)
        || !visibleSegment(targetLead, target, navigationObstacles)
      ) {
        continue;
      }
      const middle = shortestVisiblePath(
        sourceLead,
        targetLead,
        navigationObstacles,
        navigationEdgeObstacles
      );
      if (!middle) continue;
      const points = simplifyPolyline(
        dedupePoints([source, ...middle, target]),
        navigationObstacles,
        navigationEdgeObstacles
      );
      if (points.length < 2) continue;

      const pairCandidates = [];
      for (const radius of [DEFAULT_CORNER_RADIUS, 8, 0]) {
        const segments = points.length === 2
          ? [portTangentCubic(source, target, sourceSide, targetSide)]
          : radius === 0
          ? points.slice(1).map((point, index) => lineAsCubic(points[index], point))
          : smoothPolyline(points, radius);
        const route = routeFromSegments({
          side: sourceSide,
          targetSide,
          segments,
          points,
          bends: Math.max(0, points.length - 2),
          kind: points.length === 2 ? 'direct' : 'obstacle',
          cornerRadius: radius,
        });
        if (!obstacles.some((rect) => routeIntersectsRect(route, rect))) {
          candidates.push(route);
          pairCandidates.push(route);
        }
      }

      // The visibility graph has selected the shortest non-crossing path for
      // the natural source and target anchors. Keep that stable port pair when
      // it satisfies both hard guarantees; exploring every alternate side is
      // expensive and often looks less connected to the branch direction.
      if (
        sourceSide === preferredSide
        && targetSide === oppositeSide(preferredSide)
        && pairCandidates.length > 0
      ) {
        const preferredCandidates = pairCandidates.map((route) => ({
          route,
          cost: scoreRoute(route, {
            nodeObstacles: obstacles,
            edgeObstacles,
            previousRoute,
            preferredSide,
          }),
        })).sort(compareCandidates);
        const clearPreferred = preferredCandidates.find(
          ({ cost }) => (
            cost.nodeIntersections === 0
            && cost.edgeCrossings === 0
          )
        );
        if (clearPreferred && isAcceptableDetour(clearPreferred.route)) {
          const selected = chooseWithHysteresis(clearPreferred);
          return {
            ...selected.route,
            sourceRect,
            targetRect,
            cost: selected.cost,
          };
        }
      }
    }
  }

  const boundedCandidates = candidates.filter((route) => (
    route === fallback || isAcceptableDetour(route)
  ));
  const scored = boundedCandidates.map((route) => ({
    route,
    cost: scoreRoute(route, {
      nodeObstacles: obstacles,
      edgeObstacles,
      previousRoute,
      preferredSide,
    }),
  })).sort(compareCandidates);
  const selected = chooseWithHysteresis(scored[0]);
  return {
    ...selected.route,
    sourceRect,
    targetRect,
    cost: selected.cost,
  };
}

/**
 * Route a visible set in stable edge-id order so relationship input ordering
 * cannot change the resulting geometry.
 */
export function findAffectedEdgeKeys({
  nodes = [],
  edges = [],
  movedNodeIds = new Set(),
  previousRoutes = new Map(),
} = {}) {
  const movedIds = movedNodeIds instanceof Set
    ? movedNodeIds
    : new Set(movedNodeIds || []);
  if (movedIds.size === 0) return new Set();
  const movedRects = nodes
    .filter((node) => movedIds.has(node.id))
    .map((node) => inflateRect(node.rect || node, DEFAULT_CLEARANCE));
  const validEdgeKeys = new Set(edges.map(
    (edge) => `${edge.parentId}::${edge.childId}`
  ));
  const affected = new Set(
    [...previousRoutes.entries()]
      .filter(([key, route]) => route.liveAffected && validEdgeKeys.has(key))
      .map(([key]) => key)
  );

  edges.forEach((edge) => {
    const key = `${edge.parentId}::${edge.childId}`;
    if (movedIds.has(edge.parentId) || movedIds.has(edge.childId)) {
      affected.add(key);
      return;
    }
    const previousRoute = previousRoutes.get(key);
    if (
      !previousRoute
      || movedRects.some((rect) => routeIntersectsRect(previousRoute, rect))
    ) {
      affected.add(key);
    }
  });
  return affected;
}

function rectGeometryMatches(left, right) {
  if (!left || !right) return false;
  return Math.abs(left.x - right.x) <= EPSILON
    && Math.abs(left.y - right.y) <= EPSILON
    && Math.abs(left.width - right.width) <= EPSILON
    && Math.abs(left.height - right.height) <= EPSILON;
}

/**
 * Marks only new edges or edges whose endpoint rectangles changed. Viewport
 * pan/zoom can add many already-routed edges to the mounted graph without
 * changing their world geometry; retaining those paths avoids a complete
 * obstacle-routing pass at every zoom step.
 */
export function findGeometryAffectedEdgeKeys({
  nodes = [],
  edges = [],
  previousRoutes = new Map(),
} = {}) {
  const rects = new Map(nodes.map((node) => [node.id, node.rect || node]));
  const nodeIndex = createSpatialIndex(nodes, { getRect: (node) => node.rect || node });
  const affected = new Set();
  edges.forEach((edge) => {
    const key = `${edge.parentId}::${edge.childId}`;
    const previousRoute = previousRoutes.get(key);
    const endpointChanged = (
      !previousRoute
      || !rectGeometryMatches(previousRoute.sourceRect, rects.get(edge.parentId))
      || !rectGeometryMatches(previousRoute.targetRect, rects.get(edge.childId))
    );
    if (endpointChanged) {
      affected.add(key);
      return;
    }
    const nearbyNodes = nodeIndex.query(previousRoute.bounds, { padding: DEFAULT_CLEARANCE });
    if (nearbyNodes.some((node) => (
      node.id !== edge.parentId
      && node.id !== edge.childId
      && routeIntersectsRect(
        previousRoute,
        inflateRect(node.rect || node, DEFAULT_CLEARANCE)
      )
    ))) {
      affected.add(key);
    }
  });
  return affected;
}

function routingNodeEntries(nodes = []) {
  return nodes
    .map((node, sourceIndex) => ({
      id: node?.id,
      rect: node?.rect || node,
      sourceIndex,
    }))
    .filter((entry) => (
      entry.rect
      && Number.isFinite(entry.rect.x)
      && Number.isFinite(entry.rect.y)
      && Number.isFinite(entry.rect.width)
      && Number.isFinite(entry.rect.height)
      && entry.rect.width > 0
      && entry.rect.height > 0
    ));
}

function rectsOverlap(left, right) {
  return left.x < right.x + right.width - EPSILON
    && left.x + left.width > right.x + EPSILON
    && left.y < right.y + right.height - EPSILON
    && left.y + left.height > right.y + EPSILON;
}

/**
 * Identify only the nodes that actually participate in an overlap.
 *
 * A no-node-crossing route is undefined while a connection's own endpoints sit
 * in impossible geometry, but that is a property of the individual nodes, not
 * of the document. Returning the participating ids lets the renderer keep
 * obstacle routing for the rest of the map instead of abandoning every
 * connection because one pair of rectangles collides.
 */
export function findOverlappingRoutingNodeIds(nodes = []) {
  const entries = routingNodeEntries(nodes);
  const index = createSpatialIndex(entries);
  const overlapping = new Set();

  for (const leftEntry of entries) {
    const left = leftEntry.rect;
    for (const rightEntry of index.query(left)) {
      if (rightEntry.sourceIndex <= leftEntry.sourceIndex) continue;
      if (rectsOverlap(left, rightEntry.rect)) {
        overlapping.add(leftEntry.id);
        overlapping.add(rightEntry.id);
      }
    }
  }

  return overlapping;
}

export function hasOverlappingRoutingNodes(nodes = []) {
  const entries = routingNodeEntries(nodes);
  const index = createSpatialIndex(entries);

  for (const leftEntry of entries) {
    const left = leftEntry.rect;
    for (const rightEntry of index.query(left)) {
      if (rightEntry.sourceIndex <= leftEntry.sourceIndex) continue;
      if (rectsOverlap(left, rightEntry.rect)) return true;
    }
  }

  return false;
}

function branchLaneCandidates(source, targets, side, obstacles) {
  const horizontal = isHorizontalSide(side);
  const direction = side === 'left' || side === 'top' ? -1 : 1;
  const sourceValue = horizontal ? source.x : source.y;
  const targetValues = targets.map((target) => horizontal ? target.x : target.y);
  const positiveDistances = targetValues
    .map((value) => (value - sourceValue) * direction)
    .filter((distance) => distance > EPSILON);
  if (positiveDistances.length === 0) return [];
  const nearestDistance = Math.min(...positiveDistances);
  const preferredDistance = Math.max(
    Math.min(DEFAULT_LEAD, nearestDistance / 2),
    Math.min(nearestDistance * 0.45, 160)
  );
  const minimumDistance = Math.min(DEFAULT_LEAD, nearestDistance / 2);
  const maximumDistance = Math.max(minimumDistance, nearestDistance - minimumDistance);
  const distances = [
    preferredDistance,
    nearestDistance * 0.35,
    nearestDistance * 0.5,
    nearestDistance * 0.65,
  ];
  obstacles.forEach((rect) => {
    const values = horizontal
      ? [rect.x, rect.x + rect.width]
      : [rect.y, rect.y + rect.height];
    values.forEach((value) => distances.push((value - sourceValue) * direction));
  });
  return [...new Set(distances.map((distance) => Math.round(distance * 1000) / 1000))]
    .filter((distance) => distance >= minimumDistance - EPSILON
      && distance <= maximumDistance + EPSILON)
    .sort((left, right) => (
      Math.abs(left - preferredDistance) - Math.abs(right - preferredDistance)
      || left - right
    ))
    .map((distance) => ({
      lane: sourceValue + distance * direction,
      lanePenalty: Math.abs(distance - preferredDistance),
    }));
}

function routeBranchGroup({
  group,
  rects,
  nodeObstacles,
  edgeObstacles,
  previousRoutes,
}) {
  const sourceRect = rects.get(group[0].parentId);
  if (!sourceRect) return null;
  const side = connectionSide(sourceRect, rects.get(group[0].childId));
  const targetSide = oppositeSide(side);
  const source = rectSideAnchor(sourceRect, side);
  const targets = group.map((edge) => rectSideAnchor(rects.get(edge.childId), targetSide));
  const lanes = branchLaneCandidates(source, targets, side, nodeObstacles);
  const horizontal = isHorizontalSide(side);
  const candidates = [];

  lanes.forEach(({ lane, lanePenalty }) => {
    const routes = [];
    let total = lanePenalty;
    let nodeIntersections = 0;
    let edgeCrossings = 0;
    group.forEach((edge, edgeIndex) => {
      const targetRect = rects.get(edge.childId);
      const target = targets[edgeIndex];
      const points = horizontal
        ? [source, { x: lane, y: source.y }, { x: lane, y: target.y }, target]
        : [source, { x: source.x, y: lane }, { x: target.x, y: lane }, target];
      const route = routeOrthogonalPoints({
        points,
        side,
        targetSide,
        kind: 'branch',
        cornerRadius: 0,
      });
      const obstacles = nodeObstacles.filter(({ id }) => (
        id !== edge.parentId && id !== edge.childId
      )).map(({ rect }) => rect);
      const unrelatedEdges = edgeObstacles.filter(
        (obstacle) => obstacle.parentId !== edge.parentId
      );
      const cost = scoreRoute(route, {
        nodeObstacles: obstacles,
        edgeObstacles: unrelatedEdges,
        previousRoute: previousRoutes.get(`${edge.parentId}::${edge.childId}`)?.kind === 'branch'
          ? previousRoutes.get(`${edge.parentId}::${edge.childId}`)
          : null,
        preferredSide: side,
      });
      nodeIntersections += cost.nodeIntersections;
      edgeCrossings += cost.edgeCrossings;
      total += cost.total;
      routes.push({
        edge,
        route: {
          ...route,
          branchLane: lane,
          sourceRect,
          targetRect,
          cost,
        },
      });
    });
    candidates.push({ routes, total, nodeIntersections, edgeCrossings, lane });
  });

  return candidates
    .filter(({ nodeIntersections }) => nodeIntersections === 0)
    .sort((left, right) => (
      left.edgeCrossings - right.edgeCrossings
      || left.total - right.total
      || left.lane - right.lane
    ))[0] || null;
}

function routeBranchEdges({
  nodes,
  edges,
  previousRoutes,
  affectedEdgeKeys,
  avoidObstacles,
  avoidEdgeObstacles,
  allowLargeObstacleRouting,
}) {
  const rects = new Map(nodes.map((node) => [node.id, node.rect || node]));
  // As in the flowing router, an overlap invalidates the connections attached
  // to the nodes involved rather than the whole document. A shared trunk is
  // resolved per group, so a group degrades only when one of its own endpoints
  // is in impossible geometry.
  const mapRoutingFallback = !avoidObstacles
    ? null
    : !allowLargeObstacleRouting && nodes.length > MAX_OBSTACLE_ROUTING_NODES
      ? 'routing-complexity'
      : null;
  const overlappingNodeIds = avoidObstacles && !mapRoutingFallback
    ? findOverlappingRoutingNodeIds(nodes)
    : new Set();
  const edgeRoutingFallback = (edge) => (
    mapRoutingFallback
      || (overlappingNodeIds.has(edge.parentId) || overlappingNodeIds.has(edge.childId)
        ? 'overlapping-nodes'
        : null)
  );
  const groupRoutingFallback = (group) => (
    group.reduce((fallback, edge) => fallback || edgeRoutingFallback(edge), null)
  );
  const useObstacleRouting = avoidObstacles && !mapRoutingFallback;
  const nodeObstacles = useObstacleRouting
    ? nodes.map((node) => ({
        id: node.id,
        rect: inflateRect(node.rect || node, DEFAULT_CLEARANCE),
      }))
    : [];
  const orderedEdges = [...edges].filter(
    (edge) => rects.has(edge.parentId) && rects.has(edge.childId)
  ).sort((left, right) => (
    String(left.parentId).localeCompare(String(right.parentId))
    || String(left.childId).localeCompare(String(right.childId))
  ));
  const groups = new Map();
  orderedEdges.forEach((edge) => {
    const side = connectionSide(rects.get(edge.parentId), rects.get(edge.childId));
    const key = `${edge.parentId}::${side}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(edge);
  });
  const routed = new Map();
  const routedEdgeObstacles = [];

  [...groups.entries()].sort(([left], [right]) => left.localeCompare(right))
    .forEach(([, group]) => {
      const routingFallback = groupRoutingFallback(group);
      const routeThisGroup = useObstacleRouting && !routingFallback;
      const laneRouteThisGroup = routeThisGroup && avoidEdgeObstacles;
      const routingMode = laneRouteThisGroup ? 'complete' : routeThisGroup ? 'node-only' : 'boundary';
      const canReuseGroup = affectedEdgeKeys instanceof Set
        && group.every((edge) => {
          const key = `${edge.parentId}::${edge.childId}`;
          const previous = previousRoutes.get(key);
          return !affectedEdgeKeys.has(key)
            && previous
            && (previous.routingFallback || null) === routingFallback
            && previous.routingMode === routingMode
            && rectGeometryMatches(previous.sourceRect, rects.get(edge.parentId))
            && rectGeometryMatches(previous.targetRect, rects.get(edge.childId));
        });
      if (canReuseGroup) {
        group.forEach((edge) => {
          const key = `${edge.parentId}::${edge.childId}`;
          const previous = previousRoutes.get(key);
          routed.set(key, previous);
          routedEdgeObstacles.push({ ...previous, ...edge });
        });
        return;
      }
      // Disabling obstacle avoidance must not change the selected style.
      const branch = routingFallback !== 'overlapping-nodes'
        ? routeBranchGroup({
            group,
            rects,
            nodeObstacles: routeThisGroup ? nodeObstacles : [],
            edgeObstacles: laneRouteThisGroup ? routedEdgeObstacles : [],
            previousRoutes,
          })
        : null;
      if (branch) {
        branch.routes.forEach(({ edge, route }) => {
          const key = `${edge.parentId}::${edge.childId}`;
          const routedRoute = {
            ...route,
            routingFallback,
            routingMode,
          };
          routed.set(key, routedRoute);
          routedEdgeObstacles.push({ ...routedRoute, ...edge });
        });
        return;
      }

      group.forEach((edge) => {
        const key = `${edge.parentId}::${edge.childId}`;
        const route = routeRoundedEdge({
          sourceRect: rects.get(edge.parentId),
          targetRect: rects.get(edge.childId),
          nodeObstacles: routeThisGroup
            ? nodeObstacles
              .filter(({ id }) => id !== edge.parentId && id !== edge.childId)
              .map(({ rect }) => rect)
            : [],
          edgeObstacles: laneRouteThisGroup
            ? routedEdgeObstacles.filter((obstacle) => obstacle.parentId !== edge.parentId)
            : [],
          previousRoute: previousRoutes.get(key),
          clearance: 0,
        });
        const routedRoute = {
          ...route,
          routingFallback: route.routingFallback
            || routingFallback
            || 'shared-trunk-unavailable',
          routingMode: laneRouteThisGroup
            ? 'complete'
            : routeThisGroup
              ? 'node-only'
              : 'boundary',
        };
        routed.set(key, routedRoute);
        routedEdgeObstacles.push({ ...routedRoute, ...edge });
      });
    });
  return routed;
}

function routeMatchesConnectionStyle(route, style) {
  if (!route) return false;
  if (style === 'branch') return route.kind === 'branch';
  if (style === 'rounded') return route.kind === 'rounded';
  return route.kind !== 'branch' && route.kind !== 'rounded';
}

// Outer siblings turn closer to their parent than inner siblings. Keeping
// every turn before the nearest child prevents elbows from crossing the
// shorter branches or detouring through their nodes.
function roundedSiblingLanes(edges, rects) {
  const groups = new Map();
  edges.forEach(edge => {
    const source = rects.get(edge.parentId), target = rects.get(edge.childId);
    if (!source || !target) return;
    const side = connectionSide(source, target);
    const sourceAnchor = rectSideAnchor(source, side), targetAnchor = rectSideAnchor(target, oppositeSide(side));
    const across = isHorizontalSide(side) ? 'y' : 'x';
    const key = `${edge.parentId}::${side}::${Math.sign(targetAnchor[across] - sourceAnchor[across])}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ edge, side, source: sourceAnchor, target: targetAnchor });
  });
  const lanes = new Map();
  groups.forEach(group => {
    if (group.length < 2) return;
    const { source, side } = group[0];
    const axis = isHorizontalSide(side) ? 'x' : 'y', across = axis === 'x' ? 'y' : 'x';
    const direction = side === 'left' || side === 'top' ? -1 : 1;
    const nearest = Math.min(...group.map(entry => (entry.target[axis] - source[axis]) * direction));
    if (nearest <= DEFAULT_LEAD * 2) return;
    group.sort((a, b) => Math.abs(a.target[across] - source[across]) - Math.abs(b.target[across] - source[across]) || String(a.edge.childId).localeCompare(String(b.edge.childId)));
    group.forEach((entry, index) => lanes.set(`${entry.edge.parentId}::${entry.edge.childId}`,
      source[axis] + direction * (DEFAULT_LEAD + (nearest - DEFAULT_LEAD * 2) * (group.length - index) / (group.length + 1))));
  });
  return lanes;
}

// Coordinate the turn of a sibling fan instead of bending each curve at a
// different distance from its parent. Past the common turn, lines continue
// straight to their nodes, preserving their vertical (or horizontal) order.
function siblingFanRoutes(edges, rects, nodes) {
  const groups = new Map();
  for (const edge of edges) {
    const source = rects.get(edge.parentId);
    const target = rects.get(edge.childId);
    if (!source || !target) continue;
    const key = `${edge.parentId}::${connectionSide(source, target)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(edge);
  }
  const preferred = new Map();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const sourceRect = rects.get(group[0].parentId);
    const side = connectionSide(sourceRect, rects.get(group[0].childId));
    const targetSide = oppositeSide(side);
    const source = rectSideAnchor(sourceRect, side);
    const targets = group.map(edge => rectSideAnchor(rects.get(edge.childId), targetSide));
    const horizontal = isHorizontalSide(side);
    const axis = horizontal ? 'x' : 'y';
    const direction = side === 'left' || side === 'top' ? -1 : 1;
    const distance = Math.min(...targets.map(target => (target[axis] - source[axis]) * direction));
    if (distance <= 0) continue;
    for (const fraction of [1, 0.75, 0.5, 0.25, 0.125]) {
      const turn = source[axis] + distance * fraction * direction;
      const routes = targets.map(target => {
        const end = { ...target, [axis]: turn };
        const middle = (source[axis] + turn) / 2;
        const segments = [cubicSegment(source,
          { ...source, [axis]: middle }, { ...end, [axis]: middle }, end)];
        if (pointDistance(end, target) > EPSILON) segments.push(lineAsCubic(end, target));
        return routeFromSegments({ side, targetSide, segments, points: [source, end, target] });
      });
      if (routes.some((route, index) => nodes.some(node => (
        node.id !== group[index].parentId && node.id !== group[index].childId
        && routeIntersectsRect(route, inflateRect(node.rect || node))
      )))) continue;
      group.forEach((edge, index) => preferred.set(`${edge.parentId}::${edge.childId}`, routes[index]));
      break;
    }
  }
  return preferred;
}

export function routeEdges({
  nodes = [],
  edges = [],
  previousRoutes = new Map(),
  avoidObstacles = true,
  avoidEdgeObstacles = true,
  affectedEdgeKeys = null,
  allowLargeObstacleRouting = false,
  style = DEFAULT_CONNECTION_STYLE_ID,
} = {}) {
  const normalizedStyle = normalizeConnectionStyleId(style);
  const compatiblePreviousRoutes = new Map(
    [...previousRoutes.entries()].filter(([, route]) => (
      routeMatchesConnectionStyle(route, normalizedStyle)
    ))
  );
  if (normalizedStyle === 'branch') {
    return routeBranchEdges({
      nodes,
      edges,
      previousRoutes: compatiblePreviousRoutes,
      affectedEdgeKeys,
      avoidObstacles,
      avoidEdgeObstacles,
      allowLargeObstacleRouting,
    });
  }
  const rects = new Map(nodes.map((node) => [node.id, node.rect || node]));
  // A no-node-crossing route is undefined while a connection's own endpoints
  // overlap other nodes, and the visibility graph grows rapidly in that state
  // because every invalid route adds more edge-lane waypoints for the next
  // edge. That impossibility is local to the nodes involved, so only the
  // connections attached to them fall back to direct boundary curves. The rest
  // of the document keeps obstacle routing instead of losing every path to one
  // bad pair of rectangles.
  const mapRoutingFallback = !avoidObstacles
    ? null
    : !allowLargeObstacleRouting && nodes.length > MAX_OBSTACLE_ROUTING_NODES
      ? 'routing-complexity'
      : null;
  const overlappingNodeIds = avoidObstacles && !mapRoutingFallback
    ? findOverlappingRoutingNodeIds(nodes)
    : new Set();
  const edgeRoutingFallback = (edge) => (
    mapRoutingFallback
      || (overlappingNodeIds.has(edge.parentId) || overlappingNodeIds.has(edge.childId)
        ? 'overlapping-nodes'
        : null)
  );
  const useObstacleRouting = avoidObstacles && !mapRoutingFallback;
  const roundedLanes = normalizedStyle === 'rounded' ? roundedSiblingLanes(edges, rects) : new Map();
  const fanRoutes = normalizedStyle === 'flow' ? siblingFanRoutes(edges, rects, nodes) : new Map();
  const routed = new Map();
  const routedEdgeObstacles = [];
  const nodeObstacleIndex = useObstacleRouting
    ? createSpatialIndex(nodes, { getRect: (node) => node.rect || node })
    : null;
  const orderedEdges = [...edges].sort((left, right) => (
    String(left.parentId).localeCompare(String(right.parentId))
    || String(left.childId).localeCompare(String(right.childId))
  ));
  const affected = affectedEdgeKeys instanceof Set ? affectedEdgeKeys : null;
  const reusableRoutes = new Map();

  if (affected && useObstacleRouting) {
    orderedEdges.forEach((edge) => {
      const key = `${edge.parentId}::${edge.childId}`;
      const sourceRect = rects.get(edge.parentId);
      const targetRect = rects.get(edge.childId);
      const previousRoute = compatiblePreviousRoutes.get(key);
      // A reused route also has to have been produced under the same routing
      // regime. Without this, a path computed while its endpoint overlapped
      // something keeps both its degraded geometry and a stale
      // `routingFallback` tag after the overlap clears, which made the
      // rendered diagnostics disagree with the pass that produced them.
      const regimeMatches = (previousRoute?.routingFallback || null)
        === edgeRoutingFallback(edge)
        && (previousRoute?.siblingFanPath || null) === (fanRoutes.get(key)?.path || null)
        && (previousRoute?.roundedLane ?? null) === (roundedLanes.get(key) ?? null);
      if (
        !affected.has(key)
        && previousRoute
        && regimeMatches
        && rectGeometryMatches(previousRoute.sourceRect, sourceRect)
        && rectGeometryMatches(previousRoute.targetRect, targetRect)
      ) {
        reusableRoutes.set(key, {
          ...previousRoute,
          parentId: edge.parentId,
          childId: edge.childId,
          liveAffected: false,
        });
      }
    });
  }

  orderedEdges.forEach((edge) => {
    const sourceRect = rects.get(edge.parentId);
    const targetRect = rects.get(edge.childId);
    if (!sourceRect || !targetRect) return;
    const key = `${edge.parentId}::${edge.childId}`;
    const routingFallback = edgeRoutingFallback(edge);
    // An endpoint sitting inside another node has no valid clearance to route
    // from, so this connection keeps its direct curve. Its neighbours are
    // unaffected.
    const routeThisEdge = useObstacleRouting && !routingFallback;
    const laneRouteThisEdge = routeThisEdge && avoidEdgeObstacles;
    const reusableRoute = reusableRoutes.get(key);
    if (reusableRoute) {
      routed.set(key, reusableRoute);
      return;
    }
    // Siblings are coordinated as a fan above. They intentionally share their
    // parent anchor; treating them as sequential obstacles would turn the fan
    // into detours. Other branches still participate in lane avoidance.
    const naturalRoute = normalizedStyle === 'rounded'
      ? routeRoundedEdge({ sourceRect, targetRect, preferredLane: roundedLanes.get(key) })
      : routeBoundaryCurve(sourceRect, targetRect);
    const allUnrelatedEdgeObstacles = laneRouteThisEdge
      ? [
          ...reusableRoutes.values(),
          ...routedEdgeObstacles,
        ].filter((obstacle) => obstacle.parentId !== edge.parentId)
      : [];
    const unrelatedEdgeObstacles = allUnrelatedEdgeObstacles.filter((obstacle) => (
      boundsOverlapWithPadding(naturalRoute.bounds, obstacle.bounds, 112)
    ));
    const localNodeEntries = routeThisEdge
      ? nodeObstacleIndex.query(naturalRoute.bounds, { padding: 112 }).filter(
          (node) => node.id !== edge.parentId && node.id !== edge.childId
        )
      : [];
    const localNodeObstacles = localNodeEntries.map((node) => node.rect || node);
    let route = routeEdge({
      sourceRect,
      targetRect,
      nodeObstacles: localNodeObstacles,
      edgeObstacles: routeThisEdge ? unrelatedEdgeObstacles : [],
      previousRoute: routeThisEdge
        && (compatiblePreviousRoutes.get(key)?.siblingFanPath || null) === (fanRoutes.get(key)?.path || null)
        ? compatiblePreviousRoutes.get(key) || null : null,
      preferredRoute: fanRoutes.get(key),
      preferredLane: roundedLanes.get(key),
      style: normalizedStyle,
    });
    if (routeThisEdge) {
      const localNodeIds = new Set(localNodeEntries.map((node) => node.id));
      const missedObstacle = nodeObstacleIndex
        .query(route.bounds, { padding: DEFAULT_CLEARANCE })
        .find((node) => (
          node.id !== edge.parentId
          && node.id !== edge.childId
          && !localNodeIds.has(node.id)
          && routeIntersectsRect(route, inflateRect(node.rect || node, DEFAULT_CLEARANCE))
        ));
      if (missedObstacle) {
        route = routeEdge({
          sourceRect,
          targetRect,
          nodeObstacles: nodes
            .filter((node) => node.id !== edge.parentId && node.id !== edge.childId)
            .map((node) => node.rect || node),
          edgeObstacles: unrelatedEdgeObstacles,
          previousRoute: compatiblePreviousRoutes.get(key) || null,
          preferredLane: roundedLanes.get(key),
          style: normalizedStyle,
        });
      }
      const localEdgeObstacles = new Set(unrelatedEdgeObstacles);
      const missedEdgeObstacle = allUnrelatedEdgeObstacles.find((obstacle) => (
        !localEdgeObstacles.has(obstacle) && routeCrossesRoute(route, obstacle)
      ));
      if (missedEdgeObstacle) {
        route = routeEdge({
          sourceRect,
          targetRect,
          nodeObstacles: nodes
            .filter((node) => node.id !== edge.parentId && node.id !== edge.childId)
            .map((node) => node.rect || node),
          edgeObstacles: allUnrelatedEdgeObstacles,
          previousRoute: compatiblePreviousRoutes.get(key) || null,
          preferredLane: roundedLanes.get(key),
          style: normalizedStyle,
        });
      }
    }
    const routedRoute = {
      ...route,
      siblingFanPath: fanRoutes.get(key)?.path || null,
      roundedLane: roundedLanes.get(key) ?? null,
      liveAffected: Boolean(affected?.has(key)),
      routingFallback,
      routingMode: laneRouteThisEdge
        ? 'complete'
        : routeThisEdge
          ? 'node-only'
          : 'boundary',
    };
    routed.set(key, routedRoute);
    if (laneRouteThisEdge) {
      routedEdgeObstacles.push({
        ...routedRoute,
        parentId: edge.parentId,
        childId: edge.childId,
      });
    }
  });
  return routed;
}

export function findCrossingEdgeKeys({
  edges = [],
  routes = new Map(),
  rerouteLaterOnly = false,
  candidateEdgeKeys = null,
} = {}) {
  const crossingKeys = new Set();
  const routedEdges = edges.map((edge) => ({
    ...edge,
    key: `${edge.parentId}::${edge.childId}`,
    route: routes.get(`${edge.parentId}::${edge.childId}`),
  })).filter(({ route }) => Boolean(route));

  for (let leftIndex = 0; leftIndex < routedEdges.length; leftIndex += 1) {
    const left = routedEdges[leftIndex];
    for (let rightIndex = leftIndex + 1; rightIndex < routedEdges.length; rightIndex += 1) {
      const right = routedEdges[rightIndex];
      if (
        candidateEdgeKeys instanceof Set
        && !candidateEdgeKeys.has(left.key)
        && !candidateEdgeKeys.has(right.key)
      ) continue;
      if (
        left.parentId === right.parentId
        || left.parentId === right.childId
        || left.childId === right.parentId
        || left.childId === right.childId
      ) continue;
      if (routeCrossesRoute(left.route, right.route)) {
        if (!rerouteLaterOnly) crossingKeys.add(left.key);
        crossingKeys.add(right.key);
      }
    }
  }
  return crossingKeys;
}

/**
 * Dense visible graphs take the node-safe fast path first, then pay the full
 * line-lane search only for paths that geometrically cross. If the later edges
 * remain trapped, try earlier edges while holding later routes fixed. Four passes
 * let newly detoured routes participate as obstacles without turning
 * every pan or zoom into an all-edges visibility-graph calculation.
 */
export function routeEdgesAdaptively(options = {}) {
  const edges = Array.isArray(options.edges) ? options.edges : [];
  let candidateEdgeKeys = options.affectedEdgeKeys instanceof Set
    && options.affectedEdgeKeys.size > 0
    ? options.affectedEdgeKeys
    : null;
  let routes = routeEdges({
    ...options,
    avoidEdgeObstacles: false,
  });
  for (let pass = 0; pass < 4; pass += 1) {
    const crossingKeys = findCrossingEdgeKeys({
      edges: pass < 2 ? edges : [...edges].reverse(),
      routes,
      rerouteLaterOnly: true,
      candidateEdgeKeys,
    });
    if (crossingKeys.size === 0) return routes;
    routes = routeEdges({
      ...options,
      previousRoutes: routes,
      affectedEdgeKeys: crossingKeys,
      avoidEdgeObstacles: true,
    });
    candidateEdgeKeys = crossingKeys;
  }
  return routes;
}
